import { Project } from "@nova/protobuf"
import { Coordinate3D, ProjectWithMetadata } from "@nova/constants"
import {
    getPerimeterKMLForProject,
    getPerimeterOutlinesKMLForProject,
    perimeterTransformer,
} from "./transformers/cube/perimeter"
import { liveVideoFlightTransformer } from "./transformers/liveVideo"
import { lineTransformer } from "./transformers/cube/line"
import { iconTransformer } from "./transformers/cube/icon"
import { infraredFlightTransformer } from "./transformers/flight/infrared"
import { orthoFlightTransformer } from "./transformers/flight/ortho"
import { pdfTransformer } from "./transformers/cube/pdf"

type EntitiesWithTransformers = readonly [
    "infraredFlights",
    "orthoFlights",
    "perimeters",
    "lines",
    "liveVideoFlights",
    "icons",
    "PDFs",
]

const unwrapEntity = <K extends EntitiesWithTransformers[number]>(
    project: ProjectWithMetadata,
    key: K,
    entityId: string
) => {
    const entity = project[key].find((entity) => entity.id === entityId) as
        | Project[K][number]
        | undefined
    if (!entity) console.error(`No matching ${key} for id ${entityId}`)
    return entity
}

const unwrapTransformer =
    <
        K extends EntitiesWithTransformers[number],
        T extends (
            entity: Project[K][number],
            project: ProjectWithMetadata
        ) => ReturnType<T> | undefined,
    >(
        project: ProjectWithMetadata,
        key: K,
        transformer: T
    ) =>
    (entityId: string) => {
        const entity = unwrapEntity(project, key, entityId)
        return entity && transformer(entity, project)
    }

const getTransformer = (project: ProjectWithMetadata) => {
    return {
        project,
        perimeterKML: () => getPerimeterKMLForProject(project),
        perimeterOutlinesKML: () => getPerimeterOutlinesKMLForProject(project),
        infraredFlight: unwrapTransformer(project, "infraredFlights", infraredFlightTransformer),
        orthoFlight: unwrapTransformer(project, "orthoFlights", orthoFlightTransformer),
        perimeter: unwrapTransformer(project, "perimeters", perimeterTransformer),
        liveVideoFlight: (flightId: string, events: Coordinate3D[]) => {
            const flight = unwrapEntity(project, "liveVideoFlights", flightId)
            return flight && liveVideoFlightTransformer(flight, project, events)
        },
        line: unwrapTransformer(project, "lines", lineTransformer),
        icon: unwrapTransformer(project, "icons", iconTransformer),
        pdf: unwrapTransformer(project, "PDFs", pdfTransformer),
    }
}

export type ProjectTransformer = ReturnType<typeof getTransformer>
export type InfraredFlightTransformer = ReturnType<typeof infraredFlightTransformer>
export type OrthoFlightTransformer = ReturnType<typeof orthoFlightTransformer>
export type PDFTransformer = ReturnType<typeof pdfTransformer>

export { getTransformer }
